import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { sizes, formatPrice, getProduct, flagshipId, defaultSizeId, getSize } from "@/config/products";

/**
 * StickyBuyBar — móvil únicamente. Aparece cuando el usuario ha bajado
 * más allá de la vista del producto principal y no hay contexto bloqueante
 * (cart drawer o lead modal abierto). Mantiene la restricción luxury:
 * sólo se muestra si es útil, con animación suave y respetando safe-area.
 */
export default function StickyBuyBar() {
  const { t } = useLanguage();
  const { setOpen: openCart, addItem, count } = useCart();
  const [visible, setVisible] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | adding | added
  const from = Math.min(...sizes.map((s) => s.price));

  // Instant sync with mobile menu — Navbar dispatches "kelo:menu" custom event
  // (no MutationObserver lag). While menu is open, StickyBuyBar is fully hidden
  // and the exit is played (framer AnimatePresence + slide down + soft opacity).
  useEffect(() => {
    const onMenu = (e) => setMenuOpen(!!e.detail?.open);
    window.addEventListener("kelo:menu", onMenu);
    // Also read initial state (in case menu opened before mount)
    setMenuOpen(document.body.getAttribute("data-mobile-menu-open") === "1");
    return () => window.removeEventListener("kelo:menu", onMenu);
  }, []);

  useEffect(() => {
    let ticking = false;
    const compute = () => {
      const flagship = document.getElementById("flagship");
      const finalCta = document.querySelector("[data-testid=final-cta-section]");
      const y = window.scrollY;
      let show = false;
      if (flagship) {
        const r = flagship.getBoundingClientRect();
        // Aparecer cuando el hero + flagship ya han pasado (usuario en modo compra).
        show = r.bottom < 0;
      } else {
        show = y > window.innerHeight * 0.9;
      }
      // No mostrar si estamos sobre el CTA final (redundante).
      if (finalCta) {
        const r = finalCta.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.6) show = false;
      }
      setVisible(show);
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(compute);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    compute();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Vigila contextos bloqueantes (cart drawer / lead modal) para ocultarnos.
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const check = () => {
      const cartOpen = !!document.querySelector("[data-testid=cart-drawer]");
      const leadOpen = !!document.querySelector('[data-lead-modal-open="1"]');
      setBlocked(cartOpen || leadOpen);
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  const show = visible && !blocked && !menuOpen;

  return (
    <AnimatePresence mode="wait">
      {show && (
        <motion.div
          data-testid="sticky-buy-bar"
          initial={{ y: 96, opacity: 0, filter: "blur(6px)" }}
          animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
          exit={{ y: 96, opacity: 0, filter: "blur(6px)" }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 30,
            mass: 0.9,
            opacity: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
            filter: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
          }}
          className="fixed inset-x-0 bottom-0 z-30 lg:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="pointer-events-auto mx-3 mb-3 flex items-center justify-between gap-3 rounded-full border border-[#eee] bg-white/95 px-4 py-2.5 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)] backdrop-blur-xl">
            <div className="min-w-0">
              <p className="truncate font-body text-[11px] uppercase tracking-[0.16em] text-[#999]">
                {t.hero.priceFrom}
              </p>
              <p className="font-serif-display text-[15px] leading-tight text-[#111]">
                {formatPrice(from)} <span className="text-[#999]">USD</span>
              </p>
            </div>
            <button
              data-testid="sticky-buy-cta"
              disabled={status === "adding"}
              onClick={() => {
                if (count > 0) { openCart(true); return; }
                if (status !== "idle") return;
                setStatus("adding");
                try {
                  const p = getProduct(flagshipId);
                  const size = getSize(defaultSizeId);
                  addItem(p, size.label, size.price, 1);
                  if (navigator.vibrate) navigator.vibrate(8);
                  setStatus("added");
                  window.setTimeout(() => {
                    openCart(true);
                    window.dispatchEvent(new CustomEvent("kelo:cart-highlight", { detail: { productId: flagshipId, size: size.label } }));
                  }, 240);
                  window.setTimeout(() => setStatus("idle"), 1600);
                } catch (e) {
                  console.error("StickyBuyBar addItem failed", e);
                  setStatus("idle");
                }
              }}
              className="cta-breathe halo-luxe press flex shrink-0 items-center gap-2 rounded-full bg-[#111] px-5 py-2.5 font-body text-sm font-medium text-white transition-colors duration-200 active:bg-[#000] disabled:opacity-90"
            >
              {status === "added" ? (
                <>
                  <Check className="h-4 w-4" /> {t.flagship.added}
                </>
              ) : status === "adding" ? (
                <>
                  <span className="loader-dot" aria-hidden="true" />
                  {t.product?.adding || "Adding…"}
                </>
              ) : (
                <>
                  <ShoppingCart className="cart-icon h-4 w-4" strokeWidth={1.9} />
                  {count > 0 ? t.cart.title : t.flagship.addToCart}
                  {count > 0 && (
                    <span className="ml-1 rounded-full bg-white/15 px-1.5 text-[11px]">{count}</span>
                  )}
                </>
              )}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
